import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarCheck, Clock, MapPin, MessageCircle, Video } from "lucide-react";
import { PublicPage, PageIntro, CompanyContactList, GREEN, GOLD, INK } from "../../components/public/PublicChrome";
import { api } from "../../lib/api";

const INPUT = "w-full rounded-sm border bg-white px-3 py-2.5 text-[15px] outline-none focus:ring-2";
const LABEL = "block text-[12px] font-semibold tracking-[0.12em] uppercase mb-1.5";
const BORDE = { borderColor: `${GREEN}33`, "--tw-ring-color": `${GOLD}66` };
const DIAS_SEMANA = ["lu", "ma", "mi", "ju", "vi", "sá", "do"];

const clp = (n) => "$" + Number(n || 0).toLocaleString("es-CL");
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const desdeIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const mayuscula = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fechaTexto = (s) => mayuscula(desdeIso(s).toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long", year: "numeric" }));
const mesTexto = (d) => mayuscula(d.toLocaleDateString("es-CL", { month: "long", year: "numeric" }));
const errorDe = (e, porDefecto) => e?.response?.data?.detail
  ? (typeof e.response.data.detail === "string" ? e.response.data.detail : porDefecto) : porDefecto;

function Aviso({ tipo = "info", children }) {
  const estilos = tipo === "error"
    ? { borderColor: "#b4532a55", backgroundColor: "#b4532a0d", color: "#8a3a1a" }
    : { borderColor: `${GREEN}33`, backgroundColor: `${GREEN}0a`, color: GREEN };
  return <div className="rounded-sm border px-4 py-3 text-[14px] leading-relaxed" role={tipo === "error" ? "alert" : undefined} style={estilos}>{children}</div>;
}

function Calendario({ mes, setMes, dias, elegido, onElegir, minMes, maxMes, cargando }) {
  const celdas = useMemo(() => {
    const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const offset = (primero.getDay() + 6) % 7;
    const total = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
    return [...Array(offset).fill(null), ...Array.from({ length: total }, (_, i) => new Date(mes.getFullYear(), mes.getMonth(), i + 1))];
  }, [mes]);
  const puedeAtras = mes > minMes;
  const puedeAdelante = mes < maxMes;
  return (
    <div className="rounded-sm border bg-white p-4" style={{ borderColor: `${GREEN}22` }}>
      <div className="flex items-center justify-between mb-3">
        <button type="button" disabled={!puedeAtras} onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
          className="p-1.5 rounded-sm hover:bg-black/5 disabled:opacity-30" aria-label="Mes anterior"><ChevronLeft className="h-5 w-5" /></button>
        <div className="font-semibold" style={{ color: GREEN }}>{mesTexto(mes)}</div>
        <button type="button" disabled={!puedeAdelante} onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}
          className="p-1.5 rounded-sm hover:bg-black/5 disabled:opacity-30" aria-label="Mes siguiente"><ChevronRight className="h-5 w-5" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[12px] font-semibold uppercase mb-1" style={{ color: `${INK}80` }}>
        {DIAS_SEMANA.map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className={`grid grid-cols-7 gap-1 ${cargando ? "opacity-50" : ""}`}>
        {celdas.map((d, i) => {
          if (!d) return <div key={`v${i}`} />;
          const k = iso(d);
          const libre = !!dias[k];
          const activo = elegido === k;
          return (
            <button type="button" key={k} disabled={!libre} onClick={() => onElegir(k)} data-testid={`agenda-dia-${k}`}
              className="aspect-square rounded-sm text-[14px] font-medium transition-colors disabled:cursor-default"
              style={activo ? { backgroundColor: GREEN, color: "#fff" }
                : libre ? { backgroundColor: `${GOLD}26`, color: GREEN } : { color: `${INK}40` }}>
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[12px]" style={{ color: `${INK}80` }}>Días destacados: con horas disponibles. Horario de Chile.</p>
    </div>
  );
}

export default function Agendar() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [errorCarga, setErrorCarga] = useState("");
  const [modalidad, setModalidad] = useState(null);
  const [mes, setMes] = useState(null);
  const [dias, setDias] = useState({});
  const [cargandoDias, setCargandoDias] = useState(false);
  const [dia, setDia] = useState(null);
  const [hora, setHora] = useState(null);
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", empresa: "", motivo: "", acepta: false, whatsapp: false });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/agenda/publica").then(({ data }) => {
      setInfo(data);
      if (data.modalidades.length === 1) setModalidad(data.modalidades[0].clave);
      const hoy = desdeIso(data.hoy);
      setMes(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
    }).catch(() => setErrorCarga("No se pudo cargar la agenda. Intente nuevamente."));
  }, []);

  const hoy = info ? desdeIso(info.hoy) : null;
  const ultimo = info ? new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + info.ventana_dias) : null;

  const cargarDias = useCallback(async () => {
    if (!info || !mes) return;
    const desde = mes < hoy ? hoy : mes;
    const finMes = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
    const hasta = finMes > ultimo ? ultimo : finMes;
    if (hasta < desde) { setDias({}); return; }
    setCargandoDias(true);
    try {
      const { data } = await api.get("/agenda/disponibilidad", { params: { desde: iso(desde), hasta: iso(hasta) } });
      setDias(data.dias || {});
    } catch {
      setDias({});
    } finally {
      setCargandoDias(false);
    }
  }, [info, mes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { cargarDias(); }, [cargarDias]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const elegida = info?.modalidades.find((m) => m.clave === modalidad);

  const reservar = async (e) => {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      const { data } = await api.post("/agenda/reservas", { inicio: `${dia}T${hora}`, modalidad, ...form });
      if (data.url) window.location.href = data.url;
      else navigate(`/agendar/reserva/${data.id}?t=${data.token}`);
    } catch (err) {
      setError(errorDe(err, "No se pudo reservar. Intente nuevamente."));
      if (err?.response?.status === 409) { setHora(null); cargarDias(); }
      setEnviando(false);
    }
  };

  return (
    <PublicPage title="Agendar asesoría" description="Reserve una reunión de asesoría contable, tributaria o financiera con Aequor Group.">
      <PageIntro eyebrow="Asesoría" title={info?.titulo || "Agendar una reunión"}>
        <p>{info?.descripcion || "Elija el día y la hora que le acomoden y confirme su reserva en línea."}</p>
      </PageIntro>

      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-12 md:py-14 space-y-10" style={{ color: INK }} data-testid="agendar">
        {errorCarga && <Aviso tipo="error">{errorCarga}</Aviso>}
        {info && !info.activo && (
          <div className="space-y-5">
            <Aviso>La agenda en línea no está disponible en este momento. Escríbanos y coordinamos su reunión.</Aviso>
            <CompanyContactList />
          </div>
        )}

        {info?.activo && (
          <>
            <div>
              <h2 className="font-display text-2xl mb-4" style={{ color: GREEN }}>1. Modalidad</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {info.modalidades.map((m) => (
                  <button type="button" key={m.clave} onClick={() => setModalidad(m.clave)} data-testid={`agenda-modalidad-${m.clave}`}
                    className="text-left rounded-sm border bg-white p-5 transition-shadow hover:shadow-md"
                    style={{ borderColor: modalidad === m.clave ? GREEN : `${GREEN}22`, boxShadow: modalidad === m.clave ? `0 0 0 2px ${GREEN}` : undefined }}>
                    <div className="flex items-center gap-2 font-semibold text-[16px]" style={{ color: GREEN }}>
                      {m.clave === "online" ? <Video className="h-4 w-4" /> : <MapPin className="h-4 w-4" />} {m.nombre}
                    </div>
                    <div className="text-[14px] mt-1" style={{ color: `${INK}99` }}>{m.detalle}</div>
                    <div className="mt-3 flex items-center gap-4 text-[14px]">
                      <span className="inline-flex items-center gap-1" style={{ color: `${INK}b3` }}><Clock className="h-3.5 w-3.5" /> {info.duracion_min} min</span>
                      <span className="font-semibold" style={{ color: GREEN }}>{m.precio ? clp(m.precio) : "Sin costo"}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {modalidad && mes && (
              <div>
                <h2 className="font-display text-2xl mb-4" style={{ color: GREEN }}>2. Día y hora</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <Calendario mes={mes} setMes={(m) => { setMes(m); setDia(null); setHora(null); }} dias={dias} elegido={dia}
                    onElegir={(d) => { setDia(d); setHora(null); }} cargando={cargandoDias}
                    minMes={new Date(hoy.getFullYear(), hoy.getMonth(), 1)} maxMes={new Date(ultimo.getFullYear(), ultimo.getMonth(), 1)} />
                  <div>
                    {!dia ? (
                      <p className="text-[15px] pt-2" style={{ color: `${INK}99` }}>
                        {Object.keys(dias).length || cargandoDias ? "Elija un día destacado en el calendario." : "No quedan horas disponibles este mes. Revise el mes siguiente."}
                      </p>
                    ) : (
                      <>
                        <p className="font-semibold mb-3" style={{ color: GREEN }}>{fechaTexto(dia)}</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {(dias[dia] || []).map((h) => (
                            <button type="button" key={h} onClick={() => setHora(h)} data-testid={`agenda-hora-${h}`}
                              className="rounded-sm border py-2 text-[14px] font-medium"
                              style={hora === h ? { backgroundColor: GREEN, color: "#fff", borderColor: GREEN } : { borderColor: `${GREEN}33`, color: GREEN, backgroundColor: "#fff" }}>
                              {h}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {modalidad && dia && hora && (
              <form onSubmit={reservar} className="space-y-5" data-testid="agenda-form">
                <h2 className="font-display text-2xl" style={{ color: GREEN }}>3. Sus datos</h2>
                <div className="rounded-sm border px-4 py-3 text-[15px] flex flex-wrap gap-x-6 gap-y-1" style={{ borderColor: `${GOLD}66`, backgroundColor: `${GOLD}12` }}>
                  <span className="inline-flex items-center gap-1.5"><CalendarCheck className="h-4 w-4" style={{ color: GOLD }} /> {fechaTexto(dia)}, {hora} hrs</span>
                  <span>{elegida?.nombre} · {info.duracion_min} min</span>
                  <span className="font-semibold" style={{ color: GREEN }}>{elegida?.precio ? clp(elegida.precio) : "Sin costo"}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={LABEL} style={{ color: GREEN }} htmlFor="ag-nombre">Nombre</label>
                    <input id="ag-nombre" className={INPUT} style={BORDE} required minLength={2} maxLength={120} value={form.nombre} onChange={set("nombre")} />
                  </div>
                  <div>
                    <label className={LABEL} style={{ color: GREEN }} htmlFor="ag-email">Correo</label>
                    <input id="ag-email" type="email" className={INPUT} style={BORDE} required value={form.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label className={LABEL} style={{ color: GREEN }} htmlFor="ag-tel">Teléfono</label>
                    <input id="ag-tel" type="tel" className={INPUT} style={BORDE} required minLength={6} maxLength={30} placeholder="+56 9 1234 5678" value={form.telefono} onChange={set("telefono")} />
                  </div>
                  <div>
                    <label className={LABEL} style={{ color: GREEN }} htmlFor="ag-empresa">Empresa (opcional)</label>
                    <input id="ag-empresa" className={INPUT} style={BORDE} maxLength={120} value={form.empresa} onChange={set("empresa")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={LABEL} style={{ color: GREEN }} htmlFor="ag-motivo">¿En qué le podemos ayudar?</label>
                    <textarea id="ag-motivo" rows={3} className={INPUT} style={BORDE} maxLength={1000} value={form.motivo} onChange={set("motivo")} />
                  </div>
                </div>
                {info.politica && <p className="text-[13px] leading-relaxed" style={{ color: `${INK}99` }}>{info.politica}</p>}
                {info.whatsapp_recordatorios && (
                  <label className="flex items-start gap-2 text-[14px]">
                    <input type="checkbox" className="mt-1" checked={form.whatsapp} onChange={set("whatsapp")} data-testid="agenda-whatsapp" />
                    <span>Quiero recibir recordatorios de la reunión por WhatsApp en mi celular.</span>
                  </label>
                )}
                <label className="flex items-start gap-2 text-[14px]">
                  <input type="checkbox" className="mt-1" checked={form.acepta} onChange={set("acepta")} required data-testid="agenda-acepta" />
                  <span>Acepto las condiciones de la reserva y la{" "}
                    <Link to="/reembolsos" target="_blank" className="underline" style={{ color: GREEN }}>política de reembolsos y cancelación</Link>.
                  </span>
                </label>
                {error && <Aviso tipo="error">{error}</Aviso>}
                <button type="submit" disabled={enviando} data-testid="agenda-reservar"
                  className="w-full sm:w-auto rounded-sm px-7 py-3 text-[15px] font-semibold text-white disabled:opacity-60" style={{ backgroundColor: GREEN }}>
                  {enviando ? "Procesando…" : elegida?.precio ? `Pagar ${clp(elegida.precio)} y reservar` : "Reservar"}
                </button>
                {elegida?.precio > 0 && (
                  <p className="text-[13px]" style={{ color: `${INK}80` }}>
                    {info.pago === "simulado" ? "Modo de prueba: el pago se simula." : "El pago se realiza en Mercado Pago (tarjetas de crédito, débito o saldo)."} La
                    hora queda reservada por 20 minutos mientras completa el pago.
                  </p>
                )}
              </form>
            )}
          </>
        )}
      </section>
    </PublicPage>
  );
}

const TITULOS = {
  confirmada: "Reunión confirmada",
  pendiente_pago: "Pago pendiente",
  expirada: "La reserva venció",
  cancelada_sin_pago: "Reserva anulada",
  cancelada: "Reunión cancelada",
  conflicto: "Pago recibido",
};

export function ReservaAgenda() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const paymentId = params.get("payment_id") || params.get("collection_id") || "";
  const estadoMp = params.get("status") || params.get("collection_status") || "";
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/agenda/reservas/${id}`, { params: { t, payment_id: paymentId || undefined } });
      setR(data);
      return data;
    } catch (e) {
      setError(errorDe(e, "No se encontró la reserva."));
      return null;
    }
  }, [id, t, paymentId]);

  useEffect(() => {
    let vivo = true;
    let intentos = 0;
    const ciclo = async () => {
      const data = await cargar();
      intentos += 1;
      if (vivo && data?.estado === "pendiente_pago" && estadoMp === "approved" && intentos < 12) setTimeout(ciclo, 5000);
    };
    ciclo();
    return () => { vivo = false; };
  }, [cargar, estadoMp]);

  const accion = async (ruta, confirmar) => {
    if (confirmar && !window.confirm(confirmar)) return;
    setOcupado(true);
    setError("");
    try {
      const { data } = await api.post(`/agenda/reservas/${id}/${ruta}`, null, { params: { t } });
      setR(data);
    } catch (e) {
      setError(errorDe(e, "No se pudo completar la acción."));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <PublicPage title="Mi reserva" description="Estado de su reserva de asesoría.">
      <PageIntro eyebrow="Asesoría" title={r ? TITULOS[r.estado] || "Mi reserva" : "Mi reserva"} />
      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-12 space-y-6" style={{ color: INK }} data-testid="reserva-agenda">
        {error && <Aviso tipo="error">{error}</Aviso>}
        {!r && !error && <p>Cargando…</p>}
        {r && (
          <>
            {r.estado === "confirmada" && (
              <Aviso>Hola {r.nombre}, su reunión quedó confirmada. Le enviamos un correo con los detalles y la invitación para su calendario.</Aviso>
            )}
            {r.estado === "pendiente_pago" && (
              <Aviso tipo={["rejected", "failure", "null"].includes(estadoMp) ? "error" : "info"}>
                {estadoMp === "approved" ? "Estamos confirmando su pago con Mercado Pago…"
                  : ["rejected", "failure", "null"].includes(estadoMp) ? "El pago no se completó. Puede intentarlo nuevamente mientras la hora siga reservada."
                    : "Su hora está reservada mientras completa el pago."}
              </Aviso>
            )}
            {["expirada", "cancelada_sin_pago"].includes(r.estado) && (
              <Aviso tipo="error">La hora se liberó porque no se completó el pago. <Link to="/agendar" className="underline">Agende una nueva hora</Link>.</Aviso>
            )}
            {r.estado === "cancelada" && (
              <Aviso>La reunión fue cancelada. <Link to="/agendar" className="underline">Agendar una nueva hora</Link>.</Aviso>
            )}
            {r.estado === "conflicto" && (
              <Aviso>Recibimos su pago, pero la hora ya había sido tomada. Le contactaremos para coordinar otra hora o devolver la cuota.</Aviso>
            )}

            <div className="rounded-sm border bg-white p-5 text-[15px] space-y-2" style={{ borderColor: `${GREEN}22` }}>
              <div className="font-semibold" style={{ color: GREEN }}>{r.titulo}{r.profesional ? ` con ${r.profesional}` : ""}</div>
              <div>{mayuscula(r.fecha_texto)}</div>
              <div>{r.modalidad_nombre}</div>
              {r.enlace && <div>Enlace: <a href={r.enlace} target="_blank" rel="noreferrer" className="underline" style={{ color: GREEN }}>{r.enlace}</a></div>}
              {r.lugar && <div>{r.lugar}: el asesor se comunicará con usted por correo o teléfono para acordarlo.</div>}
              {r.monto > 0 && <div>Cuota: <b>{clp(r.monto)}</b></div>}
              <div className="text-[13px]" style={{ color: `${INK}80` }}>Código de reserva: {r.codigo}</div>
            </div>

            <div className="flex flex-wrap gap-3">
              {r.estado === "pendiente_pago" && r.pagar_url && (
                <a href={r.pagar_url} className="rounded-sm px-6 py-3 text-[15px] font-semibold text-white" style={{ backgroundColor: GREEN }}>
                  Pagar {clp(r.monto)}
                </a>
              )}
              {r.simulado && (
                <button type="button" disabled={ocupado} onClick={() => accion("simular-pago")} data-testid="agenda-simular-pago"
                  className="rounded-sm px-6 py-3 text-[15px] font-semibold text-white disabled:opacity-60" style={{ backgroundColor: GREEN }}>
                  Simular pago aprobado (modo prueba)
                </button>
              )}
              {r.whatsapp_url && (
                <a href={r.whatsapp_url} target="_blank" rel="noreferrer" data-testid="agenda-whatsapp-btn"
                  className="inline-flex items-center gap-2 rounded-sm px-6 py-3 text-[15px] font-semibold text-white" style={{ backgroundColor: "#1f8f4e" }}>
                  <MessageCircle className="h-4 w-4" /> Escribir por WhatsApp
                </a>
              )}
              {r.puede_cancelar && (
                <button type="button" disabled={ocupado} data-testid="agenda-cancelar"
                  onClick={() => accion("cancelar", r.estado === "confirmada" ? "¿Cancelar la reunión?" : "¿Anular la reserva?")}
                  className="rounded-sm border px-6 py-3 text-[15px] font-medium disabled:opacity-60" style={{ borderColor: `${GREEN}55`, color: GREEN }}>
                  {r.estado === "confirmada" ? "Cancelar reunión" : "Anular reserva"}
                </button>
              )}
            </div>
            {r.estado === "confirmada" && !r.puede_cancelar && (
              <p className="text-[13px]" style={{ color: `${INK}80` }}>
                Faltan menos de {r.cancelacion_horas} horas: para cambios, escríbanos{r.whatsapp ? ` por WhatsApp al ${r.whatsapp}` : ""}.
              </p>
            )}
            {r.politica && <p className="text-[13px] leading-relaxed" style={{ color: `${INK}80` }}>{r.politica}</p>}
          </>
        )}
      </section>
    </PublicPage>
  );
}
